import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

export type PullRefreshState = 'idle' | 'pulling' | 'ready' | 'refreshing' | 'completed';

export interface UsePullToRefreshOptions {
  onRefresh?: () => Promise<void> | void;
  threshold?: number;
  maxPull?: number;
  disabled?: boolean;
}

export interface UsePullToRefreshReturn {
  state: PullRefreshState;
  indicatorRef: React.RefObject<HTMLDivElement | null>;
  arrowRef: React.RefObject<SVGSVGElement | null>;
  isIOSDevice: boolean;
}

/**
 * Checks if current device is iOS (iPhone, iPad, iPod) or touch-capable Apple device
 */
export const checkIsIOSDevice = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOSPlatform = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  const isIPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isIOSPlatform || isIPadOS;
};

/**
 * Memory-efficient, zero-re-render touch pull-to-refresh hook tailored for iPhone / iOS.
 * Directly animates the DOM via GPU-accelerated translate3d during touch dragging
 * to completely eliminate React fiber allocations and VDOM reconciliation at 60/120Hz.
 */
export function usePullToRefresh({
  onRefresh,
  threshold = 65,
  maxPull = 115,
  disabled = false,
}: UsePullToRefreshOptions = {}): UsePullToRefreshReturn {
  const [state, setState] = useState<PullRefreshState>('idle');
  const location = useLocation();

  const indicatorRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<SVGSVGElement | null>(null);

  // References for zero-allocation touch tracking
  const startYRef = useRef<number>(0);
  const startXRef = useRef<number>(0);
  const currentPullRef = useRef<number>(0);
  const isPullingRef = useRef<boolean>(false);
  const isThresholdMetRef = useRef<boolean>(false);
  const rafIdRef = useRef<number | null>(null);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  // Detect iOS once
  const isIOSDeviceRef = useRef<boolean>(checkIsIOSDevice());

  // Check route-based disabling (e.g. Arena games)
  const isArenaGameRoute = useCallback((pathname: string) => {
    return (
      (pathname.startsWith('/arena/') && pathname !== '/arena') ||
      pathname.startsWith('/play/') ||
      pathname.startsWith('/game/') ||
      pathname.startsWith('/table/') ||
      pathname.startsWith('/watch/') ||
      pathname.startsWith('/room/')
    );
  }, []);

  const isRouteDisabled = isArenaGameRoute(location.pathname);

  // Update DOM transform with zero React re-render
  const applyTransform = useCallback((distance: number, progressRatio: number) => {
    if (indicatorRef.current) {
      if (distance <= 0) {
        indicatorRef.current.style.transform = 'translate3d(0, -70px, 0)';
        indicatorRef.current.style.opacity = '0';
        indicatorRef.current.style.willChange = 'auto';
        indicatorRef.current.style.transition = 'transform 0.28s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.2s ease';
      } else {
        indicatorRef.current.style.transform = `translate3d(0, ${distance}px, 0)`;
        indicatorRef.current.style.opacity = `${Math.min(1, progressRatio * 1.2)}`;
        indicatorRef.current.style.willChange = 'transform, opacity';
        indicatorRef.current.style.transition = 'none';
      }
    }

    if (arrowRef.current) {
      const rotation = Math.min(180, progressRatio * 180);
      arrowRef.current.style.transform = `rotate(${rotation}deg)`;
    }
  }, []);

  // Trigger gentle haptic tap if supported by device
  const triggerHaptic = useCallback(() => {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(12);
      } catch {
        // Silently ignore if vibrations are not permitted or supported
      }
    }
  }, []);

  // Cleanup pending animation frames and timeouts
  const clearTimers = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (refreshTimeoutRef.current !== null) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  useEffect(() => {
    if (disabled || isRouteDisabled) {
      isPullingRef.current = false;
      isRefreshingRef.current = false;
      setState('idle');
      applyTransform(0, 0);
      return;
    }

    let isTouchMoveBound = false;
    const detachTouchMove = () => {
      if (isTouchMoveBound) {
        window.removeEventListener('touchmove', onTouchMove);
        isTouchMoveBound = false;
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      detachTouchMove();
      // If already refreshing or multiple touches, ignore
      if (isRefreshingRef.current || e.touches.length !== 1) return;

      // Only initiate if scrolled to absolute top of document
      const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      if (scrollTop > 5) return;

      // Also verify we're not inside an active modal dialog
      const target = e.target as HTMLElement | null;
      if (target && typeof target.closest === 'function' && target.closest('[role="dialog"], .modal-backdrop, .modal, .shared-app-menu')) {
        return;
      }

      startYRef.current = e.touches[0].clientY;
      startXRef.current = e.touches[0].clientX;
      currentPullRef.current = 0;
      isPullingRef.current = false;
      isThresholdMetRef.current = false;

      // Attach non-passive touchmove strictly for this top pull gesture
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      isTouchMoveBound = true;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (isRefreshingRef.current || e.touches.length !== 1) return;

      const currentY = e.touches[0].clientY;
      const currentX = e.touches[0].clientX;
      const deltaY = currentY - startYRef.current;
      const deltaX = currentX - startXRef.current;

      // If user is scrolled down, do not engage
      const scrollTop = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      if (scrollTop > 5 && !isPullingRef.current) return;

      // If horizontal travel dominates, this is a swipe/carousel gesture - ignore
      if (!isPullingRef.current && Math.abs(deltaX) > Math.abs(deltaY)) {
        return;
      }

      // Engaged pulling down
      if (deltaY > 5) {
        // Prevent default browser overscroll bounce once engaged in pull-to-refresh
        if (e.cancelable) {
          e.preventDefault();
        }

        if (!isPullingRef.current) {
          isPullingRef.current = true;
          setState('pulling');
        }

        // Apply authentic iOS logarithmic resistance curve
        // Dampens pull distance so it feels tight and controlled
        const dampedY = deltaY < threshold
          ? deltaY * 0.55
          : threshold * 0.55 + (deltaY - threshold) * 0.22;

        const clampedDistance = Math.min(maxPull, dampedY);
        currentPullRef.current = clampedDistance;
        const progress = Math.min(1, clampedDistance / (threshold * 0.75));

        // Schedule visual transform via requestAnimationFrame
        if (rafIdRef.current === null) {
          rafIdRef.current = requestAnimationFrame(() => {
            applyTransform(clampedDistance, progress);
            rafIdRef.current = null;
          });
        }

        // Threshold detection with single haptic bump
        if (clampedDistance >= threshold * 0.75 && !isThresholdMetRef.current) {
          isThresholdMetRef.current = true;
          triggerHaptic();
          setState('ready');
        } else if (clampedDistance < threshold * 0.75 && isThresholdMetRef.current) {
          isThresholdMetRef.current = false;
          setState('pulling');
        }
      }
    };

    const onTouchEnd = async () => {
      detachTouchMove();
      if (!isPullingRef.current || isRefreshingRef.current) {
        isPullingRef.current = false;
        return;
      }

      isPullingRef.current = false;

      if (isThresholdMetRef.current) {
        // Transition into refreshing state
        isRefreshingRef.current = true;
        setState('refreshing');

        // Snap indicator to refreshing position (e.g. 50px)
        const refreshPos = threshold * 0.75;
        applyTransform(refreshPos, 1);

        const startTime = Date.now();

        try {
          // Execute refresh callback or global events
          if (onRefresh) {
            await onRefresh();
          } else {
            // Default global dispatch
            window.dispatchEvent(new CustomEvent('app:pull-to-refresh'));
            window.dispatchEvent(new CustomEvent('app:refresh'));
          }

          // Ensure spinner displays for a minimum perceptible duration (at least 600ms)
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, 600 - elapsed);

          if (remaining > 0) {
            await new Promise((resolve) => setTimeout(resolve, remaining));
          }

          // Complete with success state
          setState('completed');
          triggerHaptic();

          // Retract indicator smoothly after 300ms
          refreshTimeoutRef.current = setTimeout(() => {
            applyTransform(0, 0);
            setState('idle');
            isRefreshingRef.current = false;
            isThresholdMetRef.current = false;
          }, 320);
        } catch (err) {
          console.warn('[usePullToRefresh] Refresh failed:', err);
          applyTransform(0, 0);
          setState('idle');
          isRefreshingRef.current = false;
          isThresholdMetRef.current = false;
        }
      } else {
        // Snap back to top
        applyTransform(0, 0);
        setState('idle');
        isThresholdMetRef.current = false;
      }
    };

    const onTouchCancel = () => {
      detachTouchMove();
      if (!isRefreshingRef.current) {
        isPullingRef.current = false;
        isThresholdMetRef.current = false;
        applyTransform(0, 0);
        setState('idle');
      }
    };

    // Attach passive touch listeners - touchmove is attached dynamically on demand
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      detachTouchMove();
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [
    disabled,
    isRouteDisabled,
    threshold,
    maxPull,
    onRefresh,
    applyTransform,
    triggerHaptic,
  ]);

  return {
    state,
    indicatorRef,
    arrowRef,
    isIOSDevice: isIOSDeviceRef.current,
  };
}
